import React, { useState } from 'react';
import { MessageSquareIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { TextButton } from '../components/TextButton';
import { Seo } from '../components/Seo';
import { pageSeo } from '../data/seo';
import { contactJsonLd } from '../data/structuredData';
import { smsMessages } from '../utils/sms';
type Status = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const update = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (status === 'error') setStatus('idle');
  };
  return <main className="bg-canvas" data-id="element-263" data-mp="19g8qclkcbfxf">
      <Seo {...pageSeo.contact} jsonLd={contactJsonLd} data-id="element-264" data-mp="19f7foi78kvg8" />
      <div className="mx-auto max-w-shell px-5 py-14 sm:px-8 lg:py-20" data-id="element-265" data-mp="1e78qs0so4g4o">
        <div className="grid gap-14 lg:grid-cols-[1fr_0.9fr] lg:gap-20" data-id="element-266" data-mp="1j6typ0ptfytj">
          <div data-id="element-267" data-mp="1j620rj8qxp03">
            <p className="mb-4 text-xs uppercase tracking-[0.18em] text-aqua-600" data-id="element-268" data-mp="1j8o8bivn04ib">
              Contact
            </p>
            <h1 className="max-w-xl font-display text-[38px] font-semibold leading-[1.03] tracking-tightest text-ink-950 sm:text-[52px]" data-id="element-269" data-mp="qhwf8u9vbvz7">
              Have questions? Text Hydrosprings.
            </h1>
            <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-ink-700" data-id="element-270" data-mp="10gvgvsw7urcp">
              Hydrosprings is an authorized dealer of Fusion 44X, the
              world&rsquo;s first 100% chemical-free pool solution &mdash;
              engineered for longevity, wellness and prestige. This is not an
              upgrade. It&rsquo;s a replacement.
            </p>

            <div className="mt-10 rounded-2xl bg-ink-950 p-7 sm:p-9" data-id="element-271" data-mp="1m96mn5cbcltp">
              <div className="flex items-center gap-3" data-id="element-272" data-mp="1h7qwwzz6xr4z">
                <MessageSquareIcon className="h-5 w-5 text-aqua-300" aria-hidden="true" data-id="element-273" data-mp="1w62tz7qh71wo" />
                <p className="font-display text-xl font-medium text-white" data-id="element-274" data-mp="1r6zsfx9pfdtg">
                  Text us
                </p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-white/60" data-id="element-275" data-mp="yi15mmumznht">
                Texting is the fastest way to reach us. Tap below and your
                message opens pre-written &mdash; just add your pool size and
                city.
              </p>
              <div className="mt-7" data-id="element-276" data-mp="18enpp9f52ais">
                <TextButton message={smsMessages.general} variant="onDark" size="md" data-id="element-277" data-mp="kq4hsv003zr8">
                  Text us
                </TextButton>
              </div>
            </div>
          </div>

          <section aria-labelledby="inquiry-heading" className="rounded-2xl bg-paper p-7 shadow-card ring-1 ring-ink-950/8 sm:p-9" data-id="element-278" data-mp="2rcjyiq2p4ta1">
            <h2 id="inquiry-heading" className="font-display text-2xl font-semibold tracking-tight text-ink-950" data-id="element-279" data-mp="2hhs0xm9466f9">
              Send an inquiry
            </h2>
            <p className="mt-2 text-sm text-ink-600" data-id="element-280" data-mp="3a6fs1ygjnq0q">
              We reply to every message. Texting is faster.
            </p>

            {status === 'success' ? <div className="mt-8 flex flex-col items-start gap-4 rounded-xl bg-aqua-50 p-6 ring-1 ring-aqua-200" data-id="element-281" data-mp="314zi1rtn5px1">
                <CheckCircle2Icon className="h-6 w-6 text-aqua-600" aria-hidden="true" data-id="element-282" data-mp="35506846jlfha" />
                <div data-id="element-283" data-mp="1prduuc7cdye5">
                  <p className="font-display text-lg font-medium text-ink-950" data-id="element-284" data-mp="1tqw9wrchff92">
                    Inquiry received
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-700" data-id="element-285" data-mp="29z2gf1le3ck2">
                    Thanks {form.name.split(' ')[0]} &mdash; a specialist will
                    reach out shortly. Want an answer sooner?
                  </p>
                </div>
                <TextButton message={smsMessages.general} size="sm" data-id="element-286" data-mp="1v9eh25wrqnyz">
                  Text us instead
                </TextButton>
              </div> : <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate data-id="element-287" data-mp="2susmp10cuv3r">
                <div data-id="element-288" data-mp="2nybn9anpgubd">
                  <label htmlFor="name" className="block text-sm font-medium text-ink-950" data-id="element-289" data-mp="2nvz8159c0y20">
                    Name
                  </label>
                  <input id="name" name="name" type="text" value={form.name} onChange={update('name')} className="mt-2 w-full rounded-xl border border-ink-950/15 bg-white px-4 py-3 text-sm text-ink-950 transition-colors duration-150 ease-smooth placeholder:text-ink-400 focus:border-aqua-500" placeholder="Jordan Rivera" data-id="element-290" data-mp="2ezgfvv8xde6h" />
                </div>
                <div data-id="element-291" data-mp="3o45mu10yaz2e">
                  <label htmlFor="email" className="block text-sm font-medium text-ink-950" data-id="element-292" data-mp="3sx8jwj4xsqnc">
                    Email
                  </label>
                  <input id="email" name="email" type="email" value={form.email} onChange={update('email')} className="mt-2 w-full rounded-xl border border-ink-950/15 bg-white px-4 py-3 text-sm text-ink-950 transition-colors duration-150 ease-smooth placeholder:text-ink-400 focus:border-aqua-500" placeholder="you@email.com" data-id="element-293" data-mp="6oz5tj0h49cr" />
                </div>
                <div data-id="element-294" data-mp="3kro0ammswxyk">
                  <label htmlFor="message" className="block text-sm font-medium text-ink-950" data-id="element-295" data-mp="3kt8wshsoeqz2">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={5} value={form.message} onChange={update('message')} className="mt-2 w-full resize-none rounded-xl border border-ink-950/15 bg-white px-4 py-3 text-sm text-ink-950 transition-colors duration-150 ease-smooth placeholder:text-ink-400 focus:border-aqua-500" placeholder="Pool size, city, and what you'd like to know." data-id="element-296" data-mp="3tup42go72cz2" />
                </div>

                {status === 'error' && <p role="alert" className="text-sm text-red-600" data-id="element-297" data-mp="qcosysncdrh8">
                    Please fill in your name, email and message.
                  </p>}

                <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink-950 px-6 py-3.5 text-sm font-medium text-white transition-colors duration-150 ease-smooth hover:bg-ink-900 disabled:opacity-70" data-id="element-298" data-mp="1505ou7j0m6ey">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-299" data-mp="m7up2mjnqol2" />}
                  {status === 'submitting' ? 'Sending' : 'Submit inquiry'}
                </button>
                <p className="text-xs leading-relaxed text-ink-400" data-id="element-300" data-mp="h9071mtil420">
                  Protected by reCAPTCHA. The Google Privacy Policy and Terms of
                  Service apply.
                </p>
              </form>}
          </section>
        </div>
      </div>
    </main>;
}